import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { useTracking } from "@similarweb/tracking-provider";
import { IconButton, Popover, PopoverContent, PopoverTrigger, Tooltip } from "@similarweb/ui-components";

import { getExcelDataTypesConfig } from "./config";
import { ExcelExportPopoverContent } from "./ExcelExportPopoverContent";
import { useDownloadAlertsExcel } from "./useDownloadAlertsExcel";
import { useExcelExportState } from "./useExcelExportState";

export const ExcelExportButton = () => {
    const { translate } = useTranslate();
    const { track } = useTracking();
    const [isOpen, setIsOpen] = useState(false);

    const { selectedIds, resetSelection, ...contentProps } = useExcelExportState();

    const { download, isPending: isDownloadPending } = useDownloadAlertsExcel({
        onSuccess: () => {
            setIsOpen(false);
        },
    });

    const handleOpenChange = (newOpenValue: boolean) => {
        setIsOpen(newOpenValue);

        if (newOpenValue) {
            resetSelection();
            track("Button", "click", "excel export/open popup");
        }
    };

    const handleDownload = () => {
        const dataTypes = getExcelDataTypesConfig()
            .filter((dataType) => selectedIds.has(dataType.id))
            .map((dataType) => dataType.apiValue);

        track("Button", "click", `excel export/download/${dataTypes.join(",")}`);
        download(dataTypes);
    };

    return (
        <Popover
            placement="bottom-end"
            open={isOpen}
            onOpenChange={handleOpenChange}
        >
            <PopoverTrigger asChild>
                <span data-automation="alerts-excel-export-button">
                    <Tooltip content={translate("global.download.excel")}>
                        <IconButton
                            type="flat"
                            iconName="excel"
                            isLoading={isDownloadPending}
                            iconColor={colorsPalettes.blue[400]}
                        />
                    </Tooltip>
                </span>
            </PopoverTrigger>
            <PopoverContent style={{ padding: "24px" }}>
                <ExcelExportPopoverContent
                    {...contentProps}
                    selectedIds={selectedIds}
                    isDownloading={isDownloadPending}
                    onDownload={handleDownload}
                />
            </PopoverContent>
        </Popover>
    );
};
